class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
    this.dots = Array.from(this.querySelectorAll('.announcement-bar__dot'));
    this.prevBtn = this.querySelector('.announcement-bar__arrow--prev');
    this.nextBtn = this.querySelector('.announcement-bar__arrow--next');
    this.trackWrapper = this.querySelector('.announcement-bar__track-wrapper');

    this.currentIndex = 0;
    this.itemCount = this.items.length;

    this.sliderEnabled = this.dataset.enableSlider === 'true';
    this.mobileSliderEnabled = this.dataset.mobileSlider === 'true';
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;

    this.autoplayTimer = null;

    this.isSliderActive = this.computeSliderActive();

    if (this.itemCount > 1 && this.isSliderActive) {
      this.initSlider();
    }

    window.addEventListener('resize', this.debounce(() => {
      const shouldBeActive = this.computeSliderActive();
      if (shouldBeActive !== this.isSliderActive) {
        this.isSliderActive = shouldBeActive;
        if (this.isSliderActive) {
          this.initSlider();
        } else {
          this.stopAutoplay();
        }
      }
    }, 200));
  }

  computeSliderActive() {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    if (this.sliderEnabled) return true;
    if (isMobile && this.mobileSliderEnabled) return true;
    return false;
  }

  initSlider() {
    if (this.itemCount <= 1) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1);
        this.restartAutoplay();
      });
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.goTo(index);
        this.restartAutoplay();
      });
    });

    this.initSwipe();

    if (this.autoplayEnabled) {
      this.startAutoplay();
    }

    // Pause autoplay when tab is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });

    // Pause on hover/focus for accessibility
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled) this.startAutoplay();
    });
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => {
      if (this.autoplayEnabled) this.startAutoplay();
    });
  }

  goTo(index) {
    let newIndex = index;
    if (newIndex < 0) newIndex = this.itemCount - 1;
    if (newIndex >= this.itemCount) newIndex = 0;

    this.items[this.currentIndex].classList.remove('is-active');
    this.items[this.currentIndex].setAttribute('aria-hidden', 'true');

    this.items[newIndex].classList.add('is-active');
    this.items[newIndex].removeAttribute('aria-hidden');

    if (this.dots.length) {
      this.dots[this.currentIndex].classList.remove('is-active');
      this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
      this.dots[newIndex].classList.add('is-active');
      this.dots[newIndex].setAttribute('aria-selected', 'true');
    }

    this.currentIndex = newIndex;
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.itemCount <= 1) return;
    this.autoplayTimer = setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  initSwipe() {
    if (!this.trackWrapper) return;

    let startX = 0;
    let endX = 0;
    const threshold = 40;

    this.trackWrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.trackWrapper.addEventListener('touchmove', (e) => {
      endX = e.touches[0].clientX;
    }, { passive: true });

    this.trackWrapper.addEventListener('touchend', () => {
      const diff = startX - endX;
      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.goTo(this.currentIndex + 1);
        } else {
          this.goTo(this.currentIndex - 1);
        }
        this.restartAutoplay();
      }
      startX = 0;
      endX = 0;
    });
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('announcement-bar', AnnouncementBar);
